import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { FaBox, FaHeart, FaStar } from 'react-icons/fa';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "./component/Footer";
import AccountSidebar from "./component/AccountSidebar";
import api from "./api";
import { useCart } from "./context/CartContext";
import { useWishlist } from "./context/WishlistContext";

const MyWishlist = () => {
    const navigate = useNavigate();

    const storedData = localStorage.getItem("userData");
    const token      = storedData ? JSON.parse(storedData).token : null;

    const { addToCart, updateQuantity, removeFromCart, cart } = useCart();
    const { setWishlistCount } = useWishlist();

    const [loading, setLoading]                       = useState(true);
    const [wishlist, setWishlist]                     = useState([]);
    const [wishlistedProducts, setWishlistedProducts] = useState({});
    const [removingId, setRemovingId]                 = useState(null);
    const [isVisible, setIsVisible]                   = useState(false);
    const [mobileView, setMobileView]                 = useState("menu");

    const [productQuantities, setProductQuantities] = useState(() => {
        try {
            const saved = localStorage.getItem("cartQuantities");
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === "object") return parsed;
            }
        } catch (e) {}
        return {};
    });

    const productQuantitiesRef = useRef(productQuantities);

    const updateProductQty = (updater) => {
        setProductQuantities((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            productQuantitiesRef.current = next;
            try { localStorage.setItem("cartQuantities", JSON.stringify(next)); } catch (e) {}
            return next;
        });
    };

    useEffect(() => {
        if (cart.length === 0) return;
        updateProductQty((prev) => {
            const next      = { ...prev };
            const cartIdSet = new Set(cart.map((i) => i.id || i._id || i.productId));
            Object.keys(next).forEach((id) => { if (!cartIdSet.has(id)) delete next[id]; });
            cart.forEach((item) => {
                const id = item.id || item._id || item.productId;
                if (id && next[id] === undefined) next[id] = item.quantity || 1;
            });
            return next;
        });
    }, [cart]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);
   useEffect(() => {
    if (sessionStorage.getItem("mob_nav_direct") === "true") {
        sessionStorage.removeItem("mob_nav_direct");
        setMobileView("wishlist");  
    }
}, []);
    useEffect(() => {
        const fetchWishlist = async () => {
            if (!token) { setLoading(false); return; }
            try {
                setLoading(true);
                const res   = await api.get("/user/get-wishlists", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const items = res.data?.data || res.data?.products || res.data?.wishlist || [];
                if (Array.isArray(items)) {
                    setWishlist(items);
                    const map = {};
                    items.forEach((item) => {
                        const id = item._id || item?.product?._id;
                        if (id) map[id] = true;
                    });
                    setWishlistedProducts(map);
                    setWishlistCount(items.length);
                }
            } catch (error) {
                console.error("Error fetching wishlist:", error);
                setWishlist([]);
            } finally {
                setLoading(false);
            }
        };
        fetchWishlist();
    }, []);

    const toggleWishlist = async (product) => {
        if (!token) { navigate("/user-login"); return; }
        const isWishlisted = !!wishlistedProducts[product._id];
        setRemovingId(product._id);
        try {
            await api.put("/user/add-wishlist", { productId: product._id, product },
                { headers: { Authorization: `Bearer ${token}` } });
            setWishlistedProducts((prev) => ({ ...prev, [product._id]: !isWishlisted }));
            setWishlistCount((prev) => !isWishlisted ? prev + 1 : Math.max(prev - 1, 0));
            if (isWishlisted) {
                setWishlist((prev) => prev.filter((item) => item._id !== product._id));
                toast.info("Removed from wishlist", { position: "top-right", autoClose: 2000, theme: "dark" });
            } else {
                setWishlist((prev) => [...prev, product]);
                toast.success("Added to wishlist ❤️", { position: "top-right", autoClose: 2000, theme: "dark" });
            }
        } catch (err) {
            toast.error("Failed to update wishlist.", { position: "top-right", autoClose: 3000, theme: "dark" });
        } finally {
            setRemovingId(null);
        }
    };

    const handleAddToCart = async (product) => {
        if (!product || !token) { if (!token) navigate("/user-login"); return; }
        const selectedColor = product?.variants?.[0]?.color?.name;
        const selectedSize  = product?.variants?.[0]?.sizes?.[0]?.size;
        await addToCart(product, selectedColor, selectedSize, "1");
        updateProductQty((prev) => ({ ...prev, [product._id]: 1 }));
        toast.success("Added to cart successfully", { position: "top-right", autoClose: 3000, theme: "dark" });
    };

    const handleIncreaseQuantity = async (product) => {
        if (!token) { navigate("/user-login"); return; }
        const currentQty = productQuantitiesRef.current[product._id] || 1;
        const maxStock   = product?.variants?.[0]?.sizes?.[0]?.stock || 99;
        if (currentQty >= maxStock) {
            toast.warning("Maximum stock limit reached", { position: "top-right", autoClose: 2000, theme: "dark" });
            return;
        }
        const newQty = currentQty + 1;
        updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(product, product?.variants?.[0]?.color?.name, product?.variants?.[0]?.sizes?.[0]?.size, newQty);
    };

    const handleDecreaseQuantity = async (product) => {
        if (!token) { navigate("/user-login"); return; }
        const currentQty = productQuantitiesRef.current[product._id] || 1;
        const newQty     = currentQty - 1;
        if (newQty <= 0) {
            const cartItem = cart.find((item) => (item.id || item._id) === product._id);
            if (cartItem) await removeFromCart(cartItem.id || cartItem._id);
            updateProductQty((prev) => { const u = { ...prev }; delete u[product._id]; return u; });
            return;
        }
        updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(product, product?.variants?.[0]?.color?.name, product?.variants?.[0]?.sizes?.[0]?.size, newQty);
    };

    const getSelectedVariant = (product, color, size) => {
        const colorVariant = product?.variants?.find((v) => v.color.name === color);
        if (!colorVariant) return null;
        const sizeVariant  = colorVariant.sizes.find((s) => s.size === size);
        if (!sizeVariant) return null;
        return {
            color: colorVariant.color, size: sizeVariant.size,
            originalPrice: sizeVariant.originalPrice, discountedPrice: sizeVariant.discountedPrice,
            stock: sizeVariant.stock, images: colorVariant.images,
        };
    };

    const handleBuyNow = (product) => {
        if (!token) { navigate("/user-login"); return; }
        const color   = product?.variants?.[0]?.color?.name;
        const size    = product?.variants?.[0]?.sizes?.[0]?.size;
        const variant = getSelectedVariant(product, color, size);
        if (!variant) return;
        navigate("/check-out", {
            state: {
                token,
                products: { ...product, variant, quantity: "1" },
                singleProduct: "yes",
                singleProductId: product._id,
            },
        });
    };

    // Wishlist Card
    const WishlistCard = ({ item }) => {
        const image           = item.variants?.[0]?.images?.[0] || item.image || "";
        const discountedPrice = item.variants?.[0]?.sizes?.[0]?.discountedPrice;
        const originalPrice   = item.variants?.[0]?.sizes?.[0]?.originalPrice;
        const discountPct     = item.variants?.[0]?.sizes?.[0]?.discount;
        const size            = item.variants?.[0]?.sizes?.[0]?.size;
        const color           = item.variants?.[0]?.color?.name;
        const rating          = item.averageRating || item.rating || 0;
        const price           = discountedPrice || originalPrice || 0;
        const hasDiscount     = discountedPrice && originalPrice && discountedPrice < originalPrice;
        const allOutOfStock   = item.variants?.every((v) => v.sizes?.every((s) => s.stock === 0));
        const isInCart        = productQuantities.hasOwnProperty(item._id);
        const qty             = productQuantities[item._id] || 0;
        const isWishlisted    = !!wishlistedProducts[item._id];
        const isRemoving      = removingId === item._id;
        const selectedStockProduct = item?.variants?.[0]?.sizes?.[0]?.stock;

        return (
            <div className={`wl-card ${isRemoving ? "wl-removing" : ""}`}>
                <div className="wl-img-wrap" onClick={() => navigate(`/product-details/${item._id}`, { state: { items: item } })}>
                    {image ? <img src={image} alt={item.name} className="wl-img" />
                        : <div className="wl-img-placeholder"><FaBox size={32} color="#ccc" /></div>
                    }
                    {allOutOfStock && <span className="wl-oos-badge">Out of Stock</span>}
                    <button
                        className="wl-heart-btn"
                        style={{ background: isWishlisted ? "rgba(237,46,46,0.95)" : "#CEB256" }}
                        onClick={(e) => { e.stopPropagation(); toggleWishlist(item); }}
                        disabled={isRemoving}
                    >
                        {isRemoving ? <span className="spinner-border spinner-border-sm" role="status" />
                            : <FaHeart size={14} color="#fff" />
                        }
                    </button>
                    <div className="wl-rating-badge">
                        <FaStar size={11} color="#f5a623" />
                        <span>{rating.toFixed(1)}</span>
                    </div>
                </div>

                <div className="wl-body">
                    <p className="wl-name" onClick={() => navigate(`/product-details/${item._id}`, { state: { items: item } })}>
                        {item.name || item.title || "Unnamed Product"}
                    </p>
                    {(size || color) && (
                        <p className="wl-variant">
                            ({size && `Size: ${size}`}{size && color && ", "}{color && `Color: ${color}`})
                        </p>
                    )}
                    {item.description && (
                        <p className="wl-desc">
                            {item.description.split(" ").length > 4
                                ? item.description.split(" ").slice(0, 4).join(" ") + "..."
                                : item.description}
                        </p>
                    )}
                    <div className="wl-price-row">
                        {hasDiscount ? (
                            <>
                                <span className="wl-price">₹{Number(discountedPrice).toLocaleString()}</span>
                                <span className="wl-orig">₹{Number(originalPrice).toLocaleString()}</span>
                                {discountPct && <span className="wl-disc">{discountPct}% OFF</span>}
                            </>
                        ) : (
                            <span className="wl-price">₹{Number(price).toLocaleString()}</span>
                        )}
                    </div>
                    <div className="wl-button-section">
                        {isInCart && !allOutOfStock (selectedStockProduct > 0) ? (
                            <div style={{ display: "flex", gap: "8px", width: "100%", alignItems: "center" }}>
                                <div style={{ flex: 1 }} className="seller-card__qty-wrap">
                                    <button className="seller-card__qty-btn" onClick={(e) => { e.stopPropagation(); handleDecreaseQuantity(item); }}>−</button>
                                    <span className="seller-card__qty-display">{qty}</span>
                                    <button className="seller-card__qty-btn" onClick={(e) => { e.stopPropagation(); handleIncreaseQuantity(item); }}>+</button>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); handleBuyNow(item); }}
                                    style={{ flex: 1, backgroundColor: "#28a745", padding: "8px", borderRadius: "5px", color: "#fff", border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
                                    Buy Now
                                </button>
                            </div>
                        ) : (
                            <div className="wl-actions">
                                <button className="wl-btn-cart" onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }} disabled={allOutOfStock}>
                                    {allOutOfStock ? "Out of Stock" : "Add to Cart"}
                                </button>
                                <button className="wl-btn-buy" onClick={(e) => { e.stopPropagation(); handleBuyNow(item); }} disabled={allOutOfStock}>
                                    Buy Now
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const WishlistContent = () => (
        <>
            <div className="wishlist-header-bar">
                <p className="wishlist-title">
                    <FaHeart className="heart-icon" size={16} />
                    My Wishlist
                    {!loading && wishlist.length > 0 && (
                        <span className="wishlist-count-badge">
                            {wishlist.length} item{wishlist.length !== 1 ? "s" : ""}
                        </span>
                    )}
                </p>
            </div>
            {loading ? (
                <div className="loader-wrap">
                    <div className="spinner-border text-primary" role="status" />
                    <p style={{ marginTop: "12px" }}>Loading wishlist...</p>
                </div>
            ) : wishlist.length > 0 ? (
                <div className="wishlist-grid">
                    {wishlist.map((item) => <WishlistCard key={item._id} item={item} />)}
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🤍</div>
                    <p>Your wishlist is empty.<br />Save items you love to find them easily later.</p>
                    <button className="browse-btn" onClick={() => navigate("/")}>Browse Products</button>
                </div>
            )}
        </>
    );

    return (
        <>
            <ToastContainer />
            <style>{`
                .wl-page-wrap { display: flex; align-items: flex-start; min-height: 80vh; padding: 20px; gap: 20px; }
                .wl-content-area { flex: 1; min-width: 0; }

                .wl-mob-content-panel { display: none; }

                .wl-mob-header {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 13px 16px; background: #fff; border-bottom: 1px solid #f0f0f0;
                    position: sticky; top: 0; z-index: 10;
                }
                .wl-mob-back-btn {
                    width: 36px; height: 36px; border: none; background: none;
                    font-size: 28px; color: #333; cursor: pointer;
                }
                .wl-mob-title { font-size: 16px; font-weight: 700; color: #111; margin: 0; }
                .wl-mob-body { padding: 16px; background: #fff; min-height: auto; }

                /* Wishlist Styles */
                .wishlist-header-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
                .wishlist-title { font-size: 17px; font-weight: 700; color: #1a1a1a; margin: 0; display: flex; align-items: center; gap: 8px; }
                .wishlist-title .heart-icon { color: #e53935; }
                .wishlist-count-badge { background: #134813; color: #fff; font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 20px; }
                .wishlist-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 20px; }
                .wl-card { border: 3px solid #FFD700; border-radius: 6px; overflow: hidden; background: #fff; display: flex; flex-direction: column; transition: transform 0.3s, box-shadow 0.3s; }
                .wl-card:hover { transform: translateY(-8px); box-shadow: 0 8px 20px rgba(0,0,0,0.2); }
                .wl-removing { opacity: 0.4; pointer-events: none; }
                .wl-img-wrap { position: relative; width: 100%; height: 200px; background: #f5f5f5; overflow: hidden; cursor: pointer; }
                .wl-img { width: 100%; height: 100%; object-fit: cover; }
                .wl-card:hover .wl-img { transform: scale(1.04); }
                .wl-oos-badge { position: absolute; top: 10px; left: 10px; background: rgba(255,255,255,0.9); color: red; font-weight: bold; font-size: 12px; padding: 3px 8px; border-radius: 3px; }
                .wl-heart-btn { position: absolute; top: 10px; right: 10px; width: 32px; height: 32px; border-radius: 50%; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.15); }
                .wl-rating-badge { position: absolute; bottom: 8px; left: 8px; background: rgba(0,0,0,0.6); color: #fff; font-size: 11px; padding: 3px 6px; border-radius: 4px; display: flex; align-items: center; gap: 3px; }
                .wl-body { flex: 1; display: flex; flex-direction: column; padding: 10px 8px; }
                .wl-name { font-size: 14px; font-weight: 700; color: #333; margin: 0 0 4px; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .wl-variant { font-size: 13px; color: #555; margin: 0 0 4px; }
                .wl-desc { font-size: 11px; color: #666; margin-bottom: 6px; line-height: 1.4; }
                .wl-price-row { display: flex; align-items: center; flex-wrap: wrap; gap: 5px; margin-bottom: 8px; }
                .wl-price { font-size: 15px; font-weight: 700; color: #28a745; }
                .wl-orig { font-size: 13px; color: #999; text-decoration: line-through; }
                .wl-disc { font-size: 10px; font-weight: 600; color: #000; padding: 2px 6px; border-radius: 3px; }
                .wl-button-section { margin-top: auto; padding-top: 8px; }
                .wl-actions { display: flex; gap: 6px; width: 100%; }
                .wl-btn-cart, .wl-btn-buy { flex: 1; padding: 8px 4px; border: none; border-radius: 5px; font-size: 11px; font-weight: 600; cursor: pointer; }
                .wl-btn-cart { background: #CEB256; color: #fff; }
                .wl-btn-buy { background: #28a745; color: #fff; }
                .wl-btn-buy:hover:not(:disabled) { background: #218838; }

                /* Visibility Fix */
                .acct-mobile-only-wrapper { display: none; }
                .wl-page-wrap { display: flex; }
                 .browse-btn{
                     background: #c9a227;
                    color: #fff;
                    box-shadow: 0 2px 8px rgba(201, 162, 39, 0.3);
                    padding: 10px;
                    border: none;
                    border-radius: 10px;
                 }
                @media (max-width: 768px) {
                    .acct-mobile-only-wrapper { display: block; }
                    .wl-page-wrap { display: none !important; }
                    .wl-mob-content-panel { display: block; }
                    .wishlist-grid { grid-template-columns: repeat(0, 1fr); gap: 12px; }
                    .wl-img-wrap { height: 160px; }
                }
                
                @media (min-width: 769px) {
                    .wl-mob-content-panel { display: none !important; }
                    .wl-page-wrap { display: flex !important; }
                }
            `}</style>

            <main className="main__content_wrapper">

                {/* Mobile Sidebar */}
                <div className="acct-mobile-only-wrapper">
                    <AccountSidebar
                        mobileView={mobileView}
                        setMobileView={setMobileView}
                        activeMobilePath="/my-wishlist"
                        contentViewName="wishlist"
                    />
                </div>

                {/* Mobile Content */}
                <div className="wl-mob-content-panel" style={{ display: mobileView === "wishlist" ? "block" : "none" }}>
                    <div className="wl-mob-header">
                        <button className="wl-mob-back-btn" onClick={() => setMobileView("menu")}>‹</button>
                        <h2 className="wl-mob-title">My Wishlist</h2>
                        <div style={{ width: 36 }} />
                    </div>
                    <div className="wl-mob-body">
                        <WishlistContent />
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="container-fluid">
                    <div className="wl-page-wrap">
                        <AccountSidebar/>
                        <div className="wl-content-area">
                            <WishlistContent />
                        </div>
                    </div>
                </div>

            </main>

            <Footer />

            <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                style={{
                    position: "fixed", bottom: "30px", right: "30px",
                    width: "46px", height: "46px", borderRadius: "50%",
                    backgroundColor: "#ceb256", color: "#fff", border: "none",
                    zIndex: 1000, cursor: "pointer",
                    display: isVisible ? "flex" : "none",
                    alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                }}
            >
                ↑
            </button>
        </>
    );
};

export default MyWishlist;